import { Link, useParams, Navigate } from 'react-router-dom';
import { articles, categories } from '@/mocks/siteData';
import SEOHead from '@/components/feature/SEOHead';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: { '@type': 'Organization', name: 'PG征途国际' },
    publisher: { '@type': 'Organization', name: 'PG征途国际', url: 'https://pgzhengtu.com' },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://pgzhengtu.com/' },
      { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `https://pgzhengtu.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://pgzhengtu.com/article/${article.slug}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - PG征途国际下载安装`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://pgzhengtu.com/article/${article.slug}`}
        ogType="article"
        schemaData={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <main className="pt-20 pb-14">
        {/* Breadcrumb */}
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
              {category?.name || '分类'}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium truncate">{article.title}</span>
          </nav>
        </div>

        <article className="max-w-3xl mx-auto px-4 md:px-6">
          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                阅读时长 {article.readTime}
              </span>
              <Link to={`/category/${article.category}`} className="flex items-center gap-1 text-primary-600 hover:text-primary-700">
                <i className="ri-folder-line"></i>
                {category?.name}
              </Link>
            </div>
          </header>

          {/* Article Content */}
          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Article Footer */}
          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1.5 bg-background-100 text-foreground-700 rounded-lg text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/70">
              <h2 className="font-heading font-bold text-foreground-900 text-lg mb-5">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group p-4 bg-background-50 border border-background-200/70 rounded-xl hover:border-primary-200 transition-all duration-200"
                  >
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2">
                      {ra.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <style>{`
        .prose-custom h2 {
          font-size: 1.25rem;
          font-weight: 700;
          font-family: var(--font-heading);
          margin-top: 2rem;
          margin-bottom: 1rem;
          color: oklch(var(--foreground-900));
          padding-bottom: 0.5rem;
          border-bottom: 2px solid oklch(var(--primary-200));
        }
        .prose-custom h3 {
          font-size: 1.1rem;
          font-weight: 600;
          font-family: var(--font-heading);
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: oklch(var(--foreground-900));
        }
        .prose-custom p {
          margin-bottom: 1rem;
          font-size: 0.95rem;
          line-height: 1.8;
          color: oklch(var(--foreground-800));
        }
        .prose-custom strong {
          font-weight: 600;
          color: oklch(var(--foreground-900));
        }
      `}</style>
    </>
  );
}